import { memo, useCallback, useEffect, useState, useRef } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { SelectionOverlay } from "./SelectionOverlay";
import { getAdaptiveFontSize } from "../utils";

export const TextNode = memo(({ data, selected, id, width, height }) => {
  const { getNode } = useReactFlow();
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  const nodeData = data;

  // Get position from React Flow node and update when node moves
  useEffect(() => {
    const updatePosition = () => {
      const node = getNode(id);
      if (node) {
        setNodePosition(node.position);
      }
    };
    updatePosition();
    const interval = setInterval(updatePosition, 100);
    return () => clearInterval(interval);
  }, [id, getNode]);

  // Handle standard node operations (resize/rotate)
  const nodeWidth = width || 200;
  const nodeHeight = height || 100;
  const backgroundColor = nodeData.backgroundColor || "transparent";
  const rotation = nodeData.rotation || 0;
  const borderRadius = nodeData.borderRadius || 0;
  const isLocked = nodeData.locked || false;

  const handleResize = useCallback(
    (newWidth, newHeight, newPosition) => {
      const resizeEvent = new CustomEvent("nodeResize", {
        detail: {
          nodeId: id,
          nodeType: "text",
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          position: newPosition,
        },
      });
      window.dispatchEvent(resizeEvent);
    },
    [id],
  );

  const handleRotate = useCallback(
    (newRotation) => {
      const rotateEvent = new CustomEvent("nodeRotate", {
        detail: {
          nodeId: id,
          nodeType: "text",
          rotation: newRotation,
        },
      });
      window.dispatchEvent(rotateEvent);
    },
    [id],
  );

  // ref for textarea to handle cursor position
  const textareaRef = useRef(null);

  // States for double-click editing
  const [isEditing, setIsEditing] = useState(false);
  const [editableContent, setEditableContent] = useState(nodeData.content || "Double click to edit");

  // Sync internal edit state when external data changes
  useEffect(() => {
    if (!isEditing) {
      setEditableContent(nodeData.content || "Double click to edit");
    }
  }, [nodeData.content, isEditing]);

  // Set cursor to end when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const length = textareaRef.current.value.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const handleDoubleClick = useCallback((e) => {
    if (isLocked) return;
    e.stopPropagation();
    
    // If text is the default "Double click to edit", change it to "Click to edit" as requested
    if (nodeData.content === "Double click to edit") {
      setEditableContent("Click to edit");
    }
    
    setIsEditing(true);
  }, [isLocked, nodeData.content]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (editableContent !== nodeData.content) {
      const updateEvent = new CustomEvent("nodeDataUpdate", {
        detail: {
          nodeId: id,
          data: { content: editableContent },
        },
      });
      window.dispatchEvent(updateEvent);
    }
  }, [id, editableContent, nodeData.content]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === "Escape") {
      setEditableContent(nodeData.content || "Double click to edit");
      setIsEditing(false);
    }
  }, [handleBlur, nodeData.content]);

  // Advanced Styles Calculation
  const shadow = nodeData.shadowSettings || { color: "#000000", blur: 0, offsetX: 0, offsetY: 0 };
  const boxShadow = shadow.blur > 0 || shadow.offsetX !== 0 || shadow.offsetY !== 0
    ? `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${shadow.color}`
    : "none";
  const baseFontSize = nodeData.fontSize || 14;
  const fittedFontSize = getAdaptiveFontSize({
    width: nodeWidth,
    height: nodeHeight,
    text: isEditing ? editableContent : nodeData.content || "Double click to edit",
    base: baseFontSize,
    min: 4,
    max: baseFontSize,
    padding: 16,
    lineHeight: nodeData.lineHeight || 1.2,
  });

  const textStyles = {
    fontSize: fittedFontSize,
    color: nodeData.color || "#000000",
    fontFamily: nodeData.fontFamily || "Inter",
    lineHeight: nodeData.lineHeight || 1.2,
    textAlign: nodeData.textAlign || "left",
    textTransform: nodeData.textTransform || "none",
    fontWeight: nodeData.fontWeight || "normal",
    fontStyle: nodeData.fontStyle || "normal",
    textDecoration: nodeData.textDecoration || "none",
    display: "flex",
    alignItems: nodeData.verticalAlign === "center" ? "center" : nodeData.verticalAlign === "bottom" ? "flex-end" : "flex-start",
    justifyContent: nodeData.textAlign === "center" ? "center" : nodeData.textAlign === "right" ? "flex-end" : "flex-start",
    width: "100%",
    height: "100%",
    padding: "8px",
    boxSizing: "border-box",
    overflow: "hidden",
  };

  const renderPointer = () => {
    if (!nodeData.pointerDirection || nodeData.pointerDirection === "none") return null;

    const dir = nodeData.pointerDirection;
    const pWidth = nodeData.pointerWidth || 10;
    const pLength = nodeData.pointerLength || 10;
    
    let pointerStyle = {
      position: "absolute",
      width: 0,
      height: 0,
      borderStyle: "solid",
      zIndex: -1,
    };

    const bgColor = backgroundColor !== "transparent" ? backgroundColor : "white";

    if (dir === "up") {
      pointerStyle = {
        ...pointerStyle,
        bottom: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        borderWidth: `0 ${pWidth / 2}px ${pLength}px ${pWidth / 2}px`,
        borderColor: `transparent transparent ${bgColor} transparent`,
      };
    } else if (dir === "down") {
      pointerStyle = {
        ...pointerStyle,
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        borderWidth: `${pLength}px ${pWidth / 2}px 0 ${pWidth / 2}px`,
        borderColor: `${bgColor} transparent transparent transparent`,
      };
    } else if (dir === "left") {
      pointerStyle = {
        ...pointerStyle,
        right: "100%",
        top: "50%",
        transform: "translateY(-50%)",
        borderWidth: `${pWidth / 2}px ${pLength}px ${pWidth / 2}px 0`,
        borderColor: `transparent ${bgColor} transparent transparent`,
      };
    } else if (dir === "right") {
      pointerStyle = {
        ...pointerStyle,
        left: "100%",
        top: "50%",
        transform: "translateY(-50%)",
        borderWidth: `${pWidth / 2}px 0 ${pWidth / 2}px ${pLength}px`,
        borderColor: `transparent transparent transparent ${bgColor}`,
      };
    }

    return <div style={pointerStyle} />;
  };

  return (
    <div 
      className="relative w-full h-full group" 
      data-id={id}
      onDoubleClick={handleDoubleClick}
    >
      {/* NodeResizer/SelectionOverlay must be direct child of node for React Flow to work properly */}
      {!isLocked && selected && !isEditing && (
        <SelectionOverlay
          selected={selected || false}
          onRotate={handleRotate}
          onResize={handleResize}
          minWidth={50}
          minHeight={30}
          nodeId={id}
          width={nodeWidth}
          height={nodeHeight}
          rotation={rotation}
          nodeX={nodePosition.x}
          nodeY={nodePosition.y}
        />
      )}
      
      <div
        className={`relative transition-all duration-200 w-full h-full ${
          isEditing 
            ? "ring-2 ring-indigo-500 ring-offset-2" 
            : selected 
              ? "ring-1 ring-slate-300 ring-offset-1" 
              : "hover:ring-1 hover:ring-slate-200"
        }`}
        style={{
          transform: `rotate(${rotation}deg) scale(${nodeData.flippedH ? -1 : 1}, ${nodeData.flippedV ? -1 : 1})`,
          backgroundColor: backgroundColor,
          borderRadius: `${borderRadius}px`,
          boxShadow: boxShadow,
        }}
      >
        {/* Render Pointer (Callout triangle) */}
        {renderPointer()}

        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="w-full h-full bg-transparent resize-none border-none outline-none overflow-hidden"
            style={{
              ...textStyles,
              padding: "8px",
            }}
            value={editableContent}
            onChange={(e) => setEditableContent(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <div
            className="whitespace-pre-line cursor-text"
            style={textStyles}
          >
            {nodeData.content || "Double click to edit"}
          </div>
        )}

        {/* Connection handles */}
        <Handle
          type="target"
          position={Position.Top}
          className="w-2 h-2 bg-white border border-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2 h-2 bg-white border border-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
        />

        <Handle
          type="target"
          position={Position.Left}
          className="w-2 h-2 bg-white border border-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
        />

        <Handle
          type="source"
          position={Position.Right}
          className="w-2 h-2 bg-white border border-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
    </div>
  );
});
